<template>
  <a-modal @cancel="handleCancel" v-bind="$attrs" :width="width" centered>
    <template #[item]="data" v-for="item in slotsArr">
      <slot :name="item" v-bind="data"></slot>
    </template>
    <template #footer>
      <slot name="footer">
        <a-button v-if="showCancelBtn" @click="handleCancel" v-bind="cancelButtonProps">
          {{ t(`returns.${cancelText}`) }}
        </a-button>
        <a-button
          v-if="showOkBtn"
          @click="handleConfirm"
          :type="okType"
          :loading="confirmLoading"
          v-bind="okButtonProps"
          >{{ t(`returns.${okText}`) }}</a-button
        >
      </slot>
    </template>
    <slot>
      <div class="feedback-object flex">
        <div class="feedback-object-icon">
          <component :is="iconComp.name" class="object-icon-inner" :class="iconComp.class" />
        </div>
        <div class="feedback-object-content">
          <p class="object-content-text">{{ t(`returns.${subTitle}`) }}</p>
          <p class="object-content-desc">{{ desc }}</p>
        </div>
      </div>
    </slot>
  </a-modal>
</template>

<script lang="ts">
  import { defineComponent, computed } from 'vue'
  import VueTypes, { object, string } from 'vue-types'
  import { ButtonProps, ButtonType } from 'ant-design-vue/lib/button'
  import { useI18n } from 'vue-i18n'

  import {
    InfoCircleFilled,
    CloseCircleFilled,
    CheckCircleFilled,
    ExclamationCircleFilled,
  } from '@ant-design/icons-vue'

  const iconCompMap = {
    success: {
      name: 'CheckCircleFilled',
      class: 'modal-icon-success',
    },
    info: {
      name: 'InfoCircleFilled',
      class: 'modal-icon-info',
    },
    warning: {
      name: 'ExclamationCircleFilled',
      class: 'modal-icon-warning',
    },
    error: {
      name: 'CloseCircleFilled',
      class: 'modal-icon-error',
    },
  }
  export default defineComponent({
    name: 'BasicModal',
    inheritAttrs: false,
    props: {
      icon: VueTypes.oneOf(['success', 'info', 'warning', 'error'] as const),
      subTitle: VueTypes.string.def(''),
      desc: VueTypes.string.def(''),
      width: VueTypes.oneOfType<string | number | undefined>([String, VueTypes.integer]).def(360),
      cancelText: VueTypes.string.def('取消'),
      cancelButtonProps: object<ButtonProps>(),
      okButtonProps: object<ButtonProps>(),
      okText: VueTypes.string.def('确定'),
      okType: string<ButtonType>().def('primary'),
      confirmLoading: VueTypes.bool.def(false),
      showOkBtn: VueTypes.bool.def(true),
      showCancelBtn: VueTypes.bool.def(false),
    },
    emits: ['ok', 'cancel'],
    setup(props, { slots, emit }) {
      const { t } = useI18n()
      const slotsArr = Object.keys(slots).filter((item) => item !== 'default' && item !== 'footer')
      const iconComp = computed(() => {
        return props.icon ? iconCompMap[props.icon] : iconCompMap.info
      })

      function handleCancel() {
        emit('cancel')
      }

      function handleConfirm() {
        emit('ok')
      }

      return {
        slotsArr,
        iconComp,
        handleCancel,
        handleConfirm,
        t,
      }
    },
    components: {
      InfoCircleFilled,
      CloseCircleFilled,
      CheckCircleFilled,
      ExclamationCircleFilled,
    },
  })
</script>

<style lang="less" scoped>
  .feedback-object {
    .feedback-object-icon {
      font-size: 42px;
      margin-right: 13px;

      .object-icon-inner {
        display: block;
      }
    }

    .feedback-object-content {
      .object-content-desc {
        color: @--color-text-secondary;
      }
    }
  }
</style>
